import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { Oval } from "react-loader-spinner";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import ServiceForm from "./serviceForm/index";
import useServicesHelper from "../../../hooks/useServicesHelper";
import { useReduxLookups } from "hooks/useReduxLookups";

function EditServices({
  onDiscard = () => {},
  onSave = () => {},
  isLoading = false,
}) {
  const [t] = useTranslation();
  const userDetails = useSelector((state) => state.userDetails || {});
  const isVendor = userDetails?.vendorId;
  const [defaultValuesInit, setDefaultValuesInit] = useState({});
  const [handleDefaultServiceRates, setHandleDefaultServiceRates] = useState(
    {}
  );

  const [initLoading, setInitLoading] = useState(true);
  const { services, languages } = useReduxLookups(isVendor);

  const { defaultServiceRates } = useServicesHelper(isVendor);

  const handleDefaultValues = useCallback(
    (setValue) => {
      const handleDefaultRates = defaultServiceRates(
        userDetails?.userRates,
        services
      );

      const defaultLanguages = {};
      Object.keys(handleDefaultRates).forEach((serviceId, index) => {
        const languagePairKeys = handleDefaultRates[serviceId];

        Object.keys(languagePairKeys).forEach(
          (languagePairId, langPairIndex) => {
            const serviceRate = handleDefaultRates[serviceId][languagePairId];
            serviceRate.forEach((rateType, rateIndex) => {
              setValue(
                `rate-${serviceId}-${languagePairId}-${rateIndex}-${rateType.rateId}-${rateType.fromLanguage}-${rateType.toLanguage}`,
                rateType?.rate ?? 0
              );

              setValue(
                `capacity-${serviceId}-${languagePairId}-${rateIndex}-${rateType.rateId}-${rateType.fromLanguage}-${rateType.toLanguage}`,
                rateType?.capacity ?? 0
              );

              if (rateType.fromLanguage) {
                setValue(
                  `${serviceId}-${languagePairId}-from`,
                  rateType?.fromLanguage ?? ""
                );

                defaultLanguages[`${serviceId}-${languagePairId}-from`] =
                  rateType?.fromLanguage ?? "";
              }

              if (rateType.toLanguage) {
                setValue(
                  `${serviceId}-${languagePairId}-to`,
                  rateType?.toLanguage ?? ""
                );

                defaultLanguages[`${serviceId}-${languagePairId}-to`] =
                  rateType.toLanguage ?? "";
              }
            });
          }
        );
      });

      const defaultsValues = {
        services: userDetails?.userServices?.map((e) => e.id) ?? [],
        serviceRates: handleDefaultRates,
        ...defaultLanguages,
      };

      Object.keys(defaultsValues).forEach((key) => {
        setValue(key, defaultsValues[key]);
      });
      setDefaultValuesInit(defaultsValues);

      setHandleDefaultServiceRates(handleDefaultRates);
      setInitLoading(false);
    },
    [services.length]
  );

  const {
    register,
    unregister,
    handleSubmit,
    setValue,
    getValues,

    watch,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    defaultValues: defaultValuesInit,
    reValidateMode: "all",
  });

  useEffect(() => {
    if (services.length > 0) {
      handleDefaultValues(setValue);
    }
  }, [services.length]);

  if (!services.length || initLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="text-gray-500">{t("Loading...")}</div>
      </div>
    );
  }
  return (
    <form
      className="flex flex-col p-[24px] items-end gap-[20px] self-stretch"
      onSubmit={handleSubmit(onSave)}
    >
      <div className="grid items-center gap-[8px] self-stretch">
        <div className="text-[#1D1B22] font-semibold text-[30px] not-italic">
          {isVendor ? t("skills") : t("services")}
        </div>
      </div>

      <ServiceForm
        data={{ services, languages }}
        register={register}
        watch={watch}
        setValue={setValue}
        errors={errors}
        getValues={getValues}
        unregister={unregister}
        defaultServices={services.filter((elem) =>
          userDetails?.userServices.map((el) => el.id).includes(elem.id)
        )}
        defaultServicesRates={handleDefaultServiceRates}
        isVendor={isVendor}
      />

      <div className="flex items-center justify-end gap-[10px]">
        <button
          type="button"
          onClick={onDiscard}
          disabled={isLoading}
          className="cursor-pointer flex py-[14px] px-[16px] gap-[8px] justify-center items-center rounded-[6px] border-[1px] border-solid border-default-color bg-white h-[46px]"
        >
          <div className="text-base-black font-bold text-[16px] not-italic">
            {t("discard")}
          </div>
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="cursor-pointer flex py-[14px] px-[16px] gap-[8px] justify-center items-center rounded-[6px] border-[1px] border-solid border-primary bg-primary h-[46px]"
        >
          <Oval
            height={30}
            width={30}
            color="#FFF"
            visible={isLoading}
            ariaLabel="oval-loading"
            secondaryColor="#FFF"
            strokeWidth={5}
            strokeWidthSecondary={3}
          />
          <div className="text-white font-bold text-[16px] not-italic">
            {t("saveChangesCta")}
          </div>
        </button>
      </div>
    </form>
  );
}

EditServices.propTypes = {
  isVendor: PropTypes.bool,
  onEditClick: PropTypes.func,
  onSave: PropTypes.func,
  isLoading: PropTypes.bool,
};

export default EditServices;
